import type { Terminal } from '@zen/Components/TerminalSelect';
import type {
  CoreBatteries,
  CoreBookingAdditionalServiceOption,
  CoreBookingCargoMode,
  CoreBookingPartyType,
  CoreCargoInputType,
  CoreGensetInlandTransport,
  CoreHangingSystem,
  CoreMonitoringLevel,
  CorePowerRequirements,
  CoreRiskLevel,
  CoreStackable,
  Currency,
  IncotermsValue,
  LengthUnit,
  TemperatureUnit,
  VolumeUnit,
  WeightUnit
} from '@zen/graphql/types.generated';
import type { NetworksOrgLoc } from '@zen/Networks/types';
import type { ModeOfTransportValue } from '@zen/types';
import type { Nullable } from '@zen/utils/typescript';

import type { BookingDraftViaWizardQueryResult } from './graphql/bookingDraftViaWizard.generated';
import type { BookingToReplicateQueryResult } from './graphql/bookingToReplicate.generated';

export { BookingScope } from '../types';
export type {
  BookingDraftViaWizardQueryResult,
  BookingDraftViaWizardQueryVariables
} from './graphql/bookingDraftViaWizard.generated';
export type { BookingToReplicateQueryResult, BookingToReplicateQueryVariables } from './graphql/bookingToReplicate.generated';
export type {
  CoreBookingCargoInput,
  CoreBookingDraftInput,
  CoreBookingInput,
  CoreBookingInstructionsInput,
  CoreBookingInsuranceOptionInput,
  CoreBookingPartiesInput,
  CoreBookingPartyInput,
  CoreBookingRoutingInput,
  CoreBookingServiceOptionInput,
  CoreBookingServicesInput,
  CoreBookingTermsInput,
  CoreCargoCharacteristicsInput,
  CoreCargoDimensionsInput,
  CoreCargoInput,
  CoreCargoMeasurementsInput,
  CoreCargoSpecialAttributesInput,
  CoreCargoTemperaturesInput,
  CoreCargoValueOfGoodsInput,
  CoreCargoVolumesInput,
  CoreCargoWeightsInput,
  CoreCommodityInput,
  CreateCoreBookingDraftInput,
  CreateCoreBookingInput,
  DangerousGoodsInput,
  DimensionInput,
  TemperatureInput,
  VolumeInput,
  WeightInput
} from '@zen/graphql/types.generated';
export {
  AccountStatus,
  AccountType,
  CoreBatteries,
  CoreBookingAdditionalServiceOption,
  CoreBookingCargoMode,
  CoreBookingDraftStatus,
  CoreBookingModeOfTransport,
  CoreBookingPartyType,
  CoreCargoInputType,
  CoreCargoType,
  CoreGensetInlandTransport,
  CoreHangingSystem,
  CoreIncoterm,
  CoreMonitoringLevel,
  CorePaymentSource,
  CorePowerRequirements,
  CoreRiskLevel,
  CoreStackable,
  Currency,
  IncotermsValue,
  LengthUnit,
  ModeOfTransport,
  TemperatureUnit,
  VolumeUnit,
  WeightUnit
} from '@zen/graphql/types.generated';
export type { ModeOfTransportValue, Error as WizardSubmitError } from '@zen/types';
export { NOT_YET_KNOWN } from '@zen/types';

export type WizardBookingDraft = NonNullable<NonNullable<BookingDraftViaWizardQueryResult['coreBookingDraft']>['bookingDraft']>;

export type WizardBookingDraftDetails = WizardBookingDraft['bookingDetails'];

export type WizardBookingDraftCargo = NonNullable<WizardBookingDraftDetails['cargo']>;

export type WizardBookingDraftCargoItem = WizardBookingDraftCargo['cargos'][number];

export type WizardBookingDraftInnerCargo = WizardBookingDraftCargoItem['innerCargos'][number];

export type WizardBookingDraftLeafCargo = WizardBookingDraftInnerCargo['innerCargos'][number];

export type WizardBookingDraftInstructions = NonNullable<WizardBookingDraftDetails['instructions']>;

export type WizardBookingDraftParties = NonNullable<WizardBookingDraftDetails['parties']>;

export type WizardBookingDraftParty = NonNullable<WizardBookingDraftParties['parties']>[number];

export type WizardBookingDraftRouting = NonNullable<WizardBookingDraftDetails['routing']>;

export type WizardBookingDraftServices = NonNullable<WizardBookingDraftDetails['services']>;

export type WizardBookingDraftTerms = NonNullable<WizardBookingDraftDetails['terms']>;

export type WizardBookingDraftContent = Pick<WizardBookingDraft, 'account' | 'bookingDetails'>;

export type WizardBookingToReplicate = NonNullable<NonNullable<BookingToReplicateQueryResult['coreBooking']>['booking']>;

export type WizardBookingToReplicateCargoDetails = Pick<WizardBookingToReplicate, 'cargoMode' | 'cargos' | 'mainModeOfTransport'>;

export type WizardBookingToReplicateCargo = WizardBookingToReplicate['cargos'][number];

export type WizardBookingToReplicateInnerCargo = WizardBookingToReplicateCargo['innerCargos'][number];

export type WizardBookingToReplicateLeafCargo = WizardBookingToReplicateInnerCargo['innerCargos'][number];

export type WizardBookingToReplicateJourney = NonNullable<WizardBookingToReplicate['cargoJourneyDetails'][number]>;

export type WizardBookingToReplicateParty = WizardBookingToReplicate['parties'][number];

export type WizardBookingToReplicateServices = NonNullable<WizardBookingToReplicate['services']>;

export type WizardBookingToReplicateTerminal = NonNullable<WizardBookingToReplicate['originTerminal']>;

export type WizardBookingToReplicateCommodity = WizardBookingToReplicateCargo['commodities'][number];

export type WizardBookingToReplicateDangerousGoods = WizardBookingToReplicateCargo['dangerousGoodsSpecs'][number];

export type WizardBookingToReplicateStop = NonNullable<WizardBookingToReplicateJourney['collectionStop']>;

export type WizardBookingToReplicateServiceOption = NonNullable<WizardBookingToReplicateServices['customsClearance']>;

export type WizardBookingToReplicateInsuranceOption = NonNullable<WizardBookingToReplicateServices['insuranceCoverage']>;

export type WizardBookingDraftCommodity = WizardBookingDraftCargoItem['commodities'][number];

export type WizardBookingDraftDangerousGoods = WizardBookingDraftCargoItem['dangerousGoodsSpecs'][number];

export type WizardBookingDraftServiceOption = NonNullable<WizardBookingDraftServices['customsClearance']>;

export type WizardBookingDraftInsuranceOption = NonNullable<WizardBookingDraftServices['insuranceCoverage']>;

export type WizardBookingDraftTerminal = NonNullable<WizardBookingDraftRouting['originTerminal']>;

export interface ReplicatedCargo<T> {
  cargo: T;
  omittedCargoCount: number;
}

export interface ReplicatedCargos<T> {
  cargos: T[];
  omittedCargoCount: number;
}

export interface ReplicatedBookingCargo {
  cargo: WizardBookingDraftCargo;
  copiedCargoIds: string[];
  omittedCargoCount: number;
}

export interface ReplicatedBookingDraft {
  draft: WizardBookingDraftContent;
  omittedCargoCount: number;
}

export enum CargoHandlingType {
  GENERAL = 'GENERAL',
  SPECIAL = 'SPECIAL'
}

export enum CargoWeightQualifier {
  GROSS = 'GROSS',
  NET = 'NET'
}

export enum InnerCargoKind {
  LOOSE = 'LOOSE',
  PALLET = 'PALLET'
}

export enum JourneyPart {
  DESTINATION = 'DESTINATION',
  ORIGIN = 'ORIGIN'
}

export enum LooseCargoVariant {
  ITEMISED = 'ITEMISED',
  TOTALS = 'TOTALS'
}

export enum LithiumPackingOption {
  CONTAINED_IN_EQUIPMENT = 'CONTAINED_IN_EQUIPMENT',
  IN_BATTERY_POWERED_VEHICLES = 'IN_BATTERY_POWERED_VEHICLES',
  PACKED_LOOSE_OR_POWER_BANKS = 'PACKED_LOOSE_OR_POWER_BANKS',
  PACKED_WITH_EQUIPMENT = 'PACKED_WITH_EQUIPMENT'
}

export enum PaymentTerms {
  COLLECT = 'COLLECT',
  PREPAID = 'PREPAID'
}

export enum PricingOption {
  PRE_AGREED_RATES = 'PRE_AGREED_RATES',
  QUOTE = 'QUOTE'
}

export enum WizardStep {
  CARGO = 'CARGO',
  INSTRUCTIONS = 'INSTRUCTIONS',
  PARTIES = 'PARTIES',
  REVIEW = 'REVIEW',
  ROUTING = 'ROUTING',
  SERVICES = 'SERVICES',
  TERMS = 'TERMS'
}

export interface WizardStepConfig {
  id: WizardStep;
  label: string;
  subtitle: string;
  title: string;
}

export interface WizardSetupValues {
  accountId: Nullable<string>;
  accountName: Nullable<string>;
  incoterm: Nullable<IncotermsValue>;
  modeOfTransport: Nullable<ModeOfTransportValue>;
  paymentTerms: Nullable<PaymentTerms>;
  pricing: Nullable<PricingOption>;
}

export interface WizardCargoDimensionsValues {
  height: Nullable<number>;
  length: Nullable<number>;
  unit: LengthUnit;
  width: Nullable<number>;
}

export interface WizardCargoWeightValues {
  qualifier: CargoWeightQualifier;
  unit: WeightUnit;
  value: Nullable<number>;
}

export interface WizardCargoVolumeValues {
  unit: VolumeUnit;
  value: Nullable<number>;
}

export interface WizardCargoValueValues {
  currency: Currency;
  value: Nullable<number>;
}

export interface DeclaredCargoValue {
  currency: Currency;
  value: number;
}

export interface WizardInnerCargoValues {
  cargoType: Nullable<CoreCargoInputType>;
  cargoValue: WizardCargoValueValues;
  commodity: string;
  dimensions: WizardCargoDimensionsValues;
  goodsDescription: string;
  id: string;
  innerCargos: WizardInnerCargoValues[];
  kind: InnerCargoKind;
  marksAndNumbers: string;
  quantity: number;
  tariffCode: string;
  weight: WizardCargoWeightValues;
}

export interface WizardCargoItemValues {
  cargoType: Nullable<CoreCargoInputType>;
  cargoValue: WizardCargoValueValues;
  commodity: string;
  dimensions: WizardCargoDimensionsValues;
  goodsDescription: string;
  hangingSystem: Nullable<CoreHangingSystem>;
  id: string;
  innerCargos: WizardInnerCargoValues[];
  marksAndNumbers: string;
  overDimensions: WizardCargoDimensionsValues;
  quantity: number;
  riskLevel: Nullable<CoreRiskLevel>;
  specialHandling: WizardSpecialHandlingValues;
  tariffCode: string;
  volume: WizardCargoVolumeValues;
  weight: WizardCargoWeightValues;
}

export interface WizardTemperatureValues {
  unit: TemperatureUnit;
  value: Nullable<number>;
}

export interface WizardDangerousGoodsRow {
  imoClass: Nullable<string>;
  unNumber: string;
}

export interface WizardSpecialHandlingValues {
  additionalInstructions: string;
  batteryPackingOptions: LithiumPackingOption[];
  batteryType: Nullable<CoreBatteries>;
  containsBatteries: boolean;
  containsDangerousGoods: boolean;
  dangerousGoodsRows: WizardDangerousGoodsRow[];
  gensetInlandTransport: Nullable<CoreGensetInlandTransport>;
  hasAdditionalInstructions: boolean;
  monitoringLevel: Nullable<CoreMonitoringLevel>;
  powerRequirements: Nullable<CorePowerRequirements>;
  requiresTemperatureControl: boolean;
  restrictedStacking: boolean;
  stackable: Nullable<CoreStackable>;
  temperatureMax: WizardTemperatureValues;
  temperatureMin: WizardTemperatureValues;
  temperatureTarget: WizardTemperatureValues;
}

export interface WizardCargoValues {
  cargoMode: Nullable<CoreBookingCargoMode>;
  handlingType: CargoHandlingType;
  items: WizardCargoItemValues[];
  itemsByCargoMode: Partial<Record<CoreBookingCargoMode, WizardCargoItemValues[]>>;
  looseVariant: LooseCargoVariant;
  specialHandling: WizardSpecialHandlingValues;
  specialHandlingAutoEnabled: boolean;
  specialHandlingDiffersPerItem: boolean;
  temperatureControlAutoEnabled: boolean;
}

export interface TerminalLabels {
  discharge: string;
  loading: string;
}

export interface WizardRoutingValues {
  cargoReadyDate: Nullable<string>;
  collectionLocation: Nullable<NetworksOrgLoc>;
  deliveryLocation: Nullable<NetworksOrgLoc>;
  dischargeTerminal: Nullable<Terminal>;
  freightContractId: Nullable<string>;
  isFinalDestinationUnknown: boolean;
  loadingTerminal: Nullable<Terminal>;
  requestedCollectionDate: Nullable<string>;
  requestedCollectionDateTouched: boolean;
  requiredDeliveryDate: Nullable<string>;
}

export interface WizardServiceOptionValues {
  enabled: boolean;
  option: CoreBookingAdditionalServiceOption;
}

export interface WizardInsuranceCoverageValues {
  currency: Currency;
  enabled: boolean;
  value: Nullable<number>;
}

export interface WizardServicesValues {
  customsClearance: WizardServiceOptionValues;
  haulageAndTrucking: WizardServiceOptionValues;
  insuranceCoverage: WizardInsuranceCoverageValues;
  storageAndWarehousing: WizardServiceOptionValues;
}

export type PartyOverrides = Partial<Record<CoreBookingPartyType, NetworksOrgLoc>>;

export type PartyReferences = Partial<Record<CoreBookingPartyType, string>>;

export interface SameAsConsigneeValues {
  bookingParty: boolean;
  importerOfRecord: boolean;
  notifyParty: boolean;
}

export interface WizardPartiesValues {
  additionalNotifyParty: Nullable<NetworksOrgLoc>;
  forwarder: Nullable<NetworksOrgLoc>;
  overrides: PartyOverrides;
  references: PartyReferences;
  sameAsConsignee: SameAsConsigneeValues;
}

export interface WizardInstructionsValues {
  specialInstructions: string;
}

export interface DiscardBookingCopy {
  description: string;
  header: string;
  leaveLabel: string;
  stayLabel: string;
}

export interface WizardFormValues {
  cargo: WizardCargoValues;
  instructions: WizardInstructionsValues;
  parties: WizardPartiesValues;
  routing: WizardRoutingValues;
  services: WizardServicesValues;
  setup: WizardSetupValues;
}

export interface WizardStepProps {
  onMoveToStep: (step: WizardStep) => void;
}
